import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../../components/block/TitleBlock";
import HelpHint from "../../../components/help-hint/HelpHint";
import SvgLoadingAnimation from "../../../components/loading/index";
import IconButton from "../../../components/button/IconButton";
import Button from "../../../components/button/Button";
import AcrResultsTables from "../../../components/acr/AcrResultsTables";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import {
  buildAcrTsv,
  countInProgressAcrRows,
  isAcrRowInProgress,
} from "../../../utils/acr";
// @ts-ignore - JS service（型定義なし）
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../../../app/accounts/memberships";
import {
  projectAcrApi,
  useGetProjectAcrResultsQuery,
  useBulkRescanProjectAcrMutation,
} from "../../../app/services/projectAcr";
import { isProducerGroupPosition } from "../../../utils/memberPositionUtils";
import type { AcrResultRow } from "../../../types/acr";

/** 照合中ファイルが残っている間の一覧ポーリング間隔（ms）。 */
const ACR_LIST_POLL_INTERVAL_MS = 30 * 1000;

/**
 * プロジェクト INFO 内「楽曲照合結果」ページ。
 *
 * 管理者ページ `/admin/acr` と同じ結果一覧を、プロジェクトメンバー
 * （プロデューサーグループ + クライアント責任者）向けに表示する。
 * 共有コンポーネント `AcrResultsTables` を利用し、照合あり（matched）の行
 * クリックで詳細モーダルを開ける。
 *
 * プロデューサーグループ（PRODUCER / DIRECTOR / ADMIN）には照合中の一括再
 * スキャンと各ファイルの再照合（モーダル内）を許可する。クライアント責任者
 * （CLIENT）は閲覧専用（再スキャン不可）。権限は INFO タブ表示条件と
 * バックエンド API の双方でガードする。
 */
const ProjectAcrResults: React.FC = () => {
  const { projectId } = useParams();

  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(projectId, {
    skip: !projectId || !currentUserData,
  });
  const position: string | undefined = membershipData?.data?.position;
  const isProducer = position ? isProducerGroupPosition(position) : false;

  // 照合中ファイルが残っている間のみ一覧をポーリングし、完了を自動反映する。
  // ポーリング継続の判定は、購読を作らずキャッシュを読める useQueryState で
  // 行う（query の結果を自身のオプションに使う際の循環参照を避けるため）。
  const { data: cachedResults } =
    projectAcrApi.endpoints.getProjectAcrResults.useQueryState(projectId ?? "");
  const shouldPollResults = countInProgressAcrRows(cachedResults) > 0;

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useGetProjectAcrResultsQuery(projectId ?? "", {
    skip: !projectId,
    pollingInterval: shouldPollResults ? ACR_LIST_POLL_INTERVAL_MS : 0,
  });

  const shotMedia = useMemo<AcrResultRow[]>(
    () => data?.data.shot_media ?? [],
    [data],
  );
  const shotCommentMedia = useMemo<AcrResultRow[]>(
    () => data?.data.shot_comment_media ?? [],
    [data],
  );
  const talkRoomMedia = useMemo<AcrResultRow[]>(
    () => data?.data.talk_room_media ?? [],
    [data],
  );

  const totalCount =
    shotMedia.length + shotCommentMedia.length + talkRoomMedia.length;

  // pending も「楽曲照合中」表示なので件数・一括再スキャン対象に含める
  const processingCount = useMemo(
    () =>
      shotMedia.filter(isAcrRowInProgress).length +
      shotCommentMedia.filter(isAcrRowInProgress).length +
      talkRoomMedia.filter(isAcrRowInProgress).length,
    [shotMedia, shotCommentMedia, talkRoomMedia],
  );

  const { copy } = useCopyToClipboard({
    successMessage: "表をコピーしました",
    errorMessage: "コピーに失敗しました",
  });

  const handleCopyTable = useCallback(() => {
    if (!data) return;
    const tsv = buildAcrTsv(shotMedia, shotCommentMedia, talkRoomMedia);
    void copy(tsv);
  }, [data, shotMedia, shotCommentMedia, talkRoomMedia, copy]);

  // ===== 一括再スキャン（プロデューサーのみ） =====
  const [bulkRescan, { isLoading: isRescanning }] =
    useBulkRescanProjectAcrMutation();

  const handleBulkRescan = useCallback(async () => {
    if (!projectId || isRescanning) return;
    try {
      const resp = await bulkRescan(projectId).unwrap();
      const count = resp.data.rescan_count;
      if (count > 0) {
        toast.success(`照合中 ${count} 件の再スキャンをキューに投入しました`);
      } else {
        toast.success("照合中のファイルはありませんでした");
      }
      void refetch();
    } catch (err: unknown) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "再スキャンの起動に失敗しました";
      toast.error(message);
    }
  }, [projectId, isRescanning, bulkRescan, refetch]);

  if (isLoading) return <SvgLoadingAnimation />;

  return (
    <div className="u-col u-w100 u-gap16">
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="楽曲照合結果" />
        <HelpHint description={"ショット内メディア・ショットコメント添付・トークルーム添付の ACR Cloud 楽曲照合結果を一覧表示します。照合ありの行はクリックすると詳細を確認できます。"} />
      </div>

      <div className="u-row-between u-items-center u-w100 u-gap8">
        <span className="bodytext-13 u-text-light-gray">
          {data
            ? `全${totalCount}件${isProducer ? ` / 照合中 ${processingCount}件` : ""}`
            : "読み込み中..."}
        </span>
        <div className="u-row u-items-center u-gap8">
          {isProducer && (
            <Button
              size="small"
              theme="primary"
              variant="outlined"
              leftIcon="refresh"
              onClick={() => void handleBulkRescan()}
              disabled={isRescanning || !data || processingCount === 0}
            >
              {isRescanning ? "再スキャン中..." : "照合中を一括再検索"}
            </Button>
          )}
          <IconButton
            icon="content_copy"
            size={20}
            title="表をコピー（Excel / スプレッドシートに貼り付け可能）"
            ariaLabel="表をコピー"
            onClick={handleCopyTable}
            disabled={!data}
          />
        </div>
      </div>

      <AcrResultsTables
        shotMedia={shotMedia}
        shotCommentMedia={shotCommentMedia}
        talkRoomMedia={talkRoomMedia}
        isFetching={isFetching}
        enableRescan={isProducer}
        onRescanSuccess={() => void refetch()}
      />
    </div>
  );
};

export default ProjectAcrResults;
